// src/utils/adaptiveTimeout.ts
// 🚀 SYSTÈME DE TIMEOUT ADAPTATIF SELON LA CONNEXION

interface ConnectionInfo {
  effectiveType?: '2g' | '3g' | '4g';
  downlink?: number;
  rtt?: number;
}

export class AdaptiveTimeout {
  private static connectionInfo: ConnectionInfo = {};
  private static isOnline = navigator.onLine;
  
  /**
   * 🌐 Initialiser la détection de connexion
   */
  static initialize(): void {
    // Écouter les changements de connexion
    window.addEventListener('online', () => {
      this.isOnline = true;
      this.updateConnectionInfo();
    });
    
    window.addEventListener('offline', () => {
      this.isOnline = false;
    });
    
    // API Network Information (si disponible)
    this.updateConnectionInfo();
  }
  
  /**
   * 📊 Mettre à jour les informations de connexion
   */
  private static updateConnectionInfo(): void {
    const connection = (navigator as any).connection || 
                     (navigator as any).mozConnection || 
                     (navigator as any).webkitConnection;
    
    if (connection) {
      this.connectionInfo = {
        effectiveType: connection.effectiveType,
        downlink: connection.downlink,
        rtt: connection.rtt
      };
    }
  }
  
  /**
   * ⚡ Obtenir un timeout adaptatif pour les APIs géolocalisation
   */
  static getGeolocationTimeout(): number {
    if (!this.isOnline) return 15000; // 15s hors ligne
    
    const { effectiveType, rtt } = this.connectionInfo;
    
    switch (effectiveType) {
      case '2g':
        return 20000; // 20s pour 2G
      case '3g':
        return 10000; // 10s pour 3G
      case '4g':
        return 5000;  // 5s pour 4G
      default:
        // Fallback basé sur RTT si disponible
        if (rtt) {
          if (rtt > 1000) return 15000;      // Connexion lente
          if (rtt > 500) return 10000;       // Connexion moyenne
          return 5000;                       // Connexion rapide
        }
        return 8000; // Défaut conservateur
    }
  }
  
  /**
   * 🗺️ Obtenir un timeout adaptatif pour Google Maps
   */
  static getMapsTimeout(): number {
    if (!this.isOnline) return 30000; // 30s hors ligne (fallback maps)
    
    const { effectiveType, downlink } = this.connectionInfo;
    
    switch (effectiveType) {
      case '2g':
        return 25000; // 25s pour 2G - Maps sont lourdes
      case '3g':
        return 15000; // 15s pour 3G
      case '4g':
        return 8000;  // 8s pour 4G
      default:
        // Fallback basé sur downlink si disponible
        if (downlink) {
          if (downlink < 0.5) return 25000;    // < 0.5 Mbps
          if (downlink < 2) return 15000;      // < 2 Mbps
          return 8000;                         // >= 2 Mbps
        }
        return 12000; // Défaut
    }
  }
  
  /**
   * 📡 Obtenir un timeout adaptatif pour les requêtes API
   */
  static getApiTimeout(): number {
    if (!this.isOnline) return 20000; // 20s hors ligne
    
    const { effectiveType, rtt } = this.connectionInfo;
    
    switch (effectiveType) {
      case '2g':
        return 15000; // 15s pour 2G
      case '3g':
        return 8000;  // 8s pour 3G
      case '4g':
        return 3000;  // 3s pour 4G
      default:
        if (rtt && rtt > 1000) return 12000;
        return 5000; // Défaut
    }
  }
  
  /**
   * 🎯 Obtenir les informations de connexion actuelles
   */
  static getConnectionInfo(): { 
    type: string; 
    speed: 'slow' | 'medium' | 'fast';
    isOnline: boolean;
  } {
    const { effectiveType, downlink, rtt } = this.connectionInfo;
    
    let speed: 'slow' | 'medium' | 'fast' = 'medium';
    
    if (effectiveType === '2g' || (rtt && rtt > 1000) || (downlink && downlink < 0.5)) {
      speed = 'slow';
    } else if (effectiveType === '4g' || (rtt && rtt < 200) || (downlink && downlink > 2)) {
      speed = 'fast';
    }
    
    return {
      type: effectiveType || 'unknown',
      speed,
      isOnline: this.isOnline
    };
  }
  
  /**
   * 🔄 Créer un timeout progressif avec retry
   */
  static createProgressiveTimeout<T>(
    operation: () => Promise<T>,
    maxRetries: number = 3
  ): Promise<T> {
    return new Promise(async (resolve, reject) => {
      let attempt = 0;
      
      while (attempt < maxRetries) {
        const timeout = this.getApiTimeout() * (attempt + 1); // Timeout progressif
        
        try {
          const result = await Promise.race([
            operation(),
            new Promise<never>((_, timeoutReject) => {
              setTimeout(() => timeoutReject(new Error('Timeout')), timeout);
            })
          ]);
          
          resolve(result);
          return;
        } catch (error) {
          attempt++;
          if (attempt >= maxRetries) {
            reject(error);
            return;
          }
          
          // Attendre avant retry (backoff exponentiel)
          await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
        }
      }
    });
  }
}

// Initialisation automatique
AdaptiveTimeout.initialize();

// 🌍 EXPOSITION GLOBALE POUR TESTS
if (typeof window !== 'undefined') {
  (window as any).AdaptiveTimeout = AdaptiveTimeout;
  (window as any).testTimeouts = () => {
    const geo = AdaptiveTimeout.getGeolocationTimeout();
    const maps = AdaptiveTimeout.getMapsTimeout();
    const api = AdaptiveTimeout.getApiTimeout();
    const connection = AdaptiveTimeout.getConnectionInfo();
    
    console.log('⏱️ TIMEOUTS ADAPTATIFS:');
    console.log(`📍 Géolocalisation: ${geo}ms`);
    console.log(`🗺️ Maps: ${maps}ms`);
    console.log(`📡 API: ${api}ms`);
    console.log('🌐 Connexion:', connection);
    console.log('✅ Tests Timeouts terminés');
    
    return { geo, maps, api, connection };
  };
}
